<script lang="ts">
    import { t } from "@lernetz/svelte-translate";
    import ChooseMode from "../../ui/mode/ChooseMode.svelte";
    import ClassMode from "../../ui/mode/ClassMode.svelte";
    import { router } from "@inertiajs/svelte";
    import { Icon } from "@lernetz/svelte-icon";
    import { setupSharedProps, sharedProps } from "../../setup";
    import ResultsOverlay from "../../ui/ResultsOverlay.svelte";
    import Footer from "../../ui/Footer.svelte";

    export let dashboard;
    export let priorResults;
    export let afterResults;

    setupSharedProps();

    let showMode = false;
    let type = 'prior'
    let openPriorResultsOverlay = false;
    let openAfterResultsOverlay = false;
    let showDeleteDataOverlay = false;
    let deleteDataType: string | null = null;
    let showDeleteDashboardOverlay = false;

    function aggregateResults(results) {
        const map = new Map();
        for (const result of results) {
            const rawId = result.question?.id ?? result.question?.['$oid'];
            const id = rawId?.$oid ?? String(rawId ?? '');
            if (!id) continue;
            if (map.has(id)) {
                map.get(id).right += result.right;
                map.get(id).wrong += result.wrong;
            } else {
                map.set(id, { ...result, right: result.right, wrong: result.wrong });
            }
        }
        return Array.from(map.values());
    }

    $: aggPrior = aggregateResults(priorResults);
    $: aggAfter = aggregateResults(afterResults);

    $: { dashboard.priorResults = aggPrior; dashboard.afterResults = aggAfter; }

    $: priorRight = aggPrior.reduce((s, r) => s + r.right, 0);
    $: priorWrong = aggPrior.reduce((s, r) => s + r.wrong, 0);
    $: afterRight = aggAfter.reduce((s, r) => s + r.right, 0);
    $: afterWrong = aggAfter.reduce((s, r) => s + r.wrong, 0);

    $: priorPpl = aggPrior.length > 0 ? aggPrior[0].right + aggPrior[0].wrong : 0;
    $: afterPpl = aggAfter.length > 0 ? aggAfter[0].right + aggAfter[0].wrong : 0;

    $: priorPercent = Math.round(priorRight / (priorRight + priorWrong) * 100) | 0;
    $: afterPercent = Math.round(afterRight / (afterRight + afterWrong) * 100) | 0;

    $: percentChange = afterPercent - priorPercent;

    function openMode(t) {
        type = t;
        showMode = true;
    }

    function closeMode() {
        showMode = false;
    }

    function deleteDashboard() {
        router.post('/deleteDashboard', dashboard)
    }

    function deleteData(type) {
        router.post('/deleteData', {dashboardId: dashboard.id, type: type})
    }

    function openDeleteData(type) {
        deleteDataType = type;
        showDeleteDataOverlay = true;
    }

    function openDeleteDashboard() {
        showDeleteDashboardOverlay = true;
    }

    function openPriorResults() {
      openPriorResultsOverlay = true;
    }

    function openAfterResults() {
      openAfterResultsOverlay = true;
    }

</script>

{#if openPriorResultsOverlay}
  <ResultsOverlay title="results prior knowledge" results={aggPrior} ppl={priorPpl} onClose={() => openPriorResultsOverlay = false} />
{/if}

{#if showDeleteDataOverlay}
  <div class="fixed inset-0 flex items-start justify-center z-50 px-16 py-24 overflow-y-auto" style="background: rgba(0,0,0,0.5)">
    <div class="bg-white rounded-3xl relative p-24 flex flex-col w-full md:w-384 items-center">
      <div class="flex place-content-between mb-16">
        <div class="text-22 mt-12 font-bold">
          {$t('delete results')}
        </div>
      </div>
      <div class="bg-false-light p8 rounded-3xl w-96 h-96 mt-24 flex items-center justify-center">
        <Icon name="arrow_refresh" class="w-64 h-64 place-self-center text-white"></Icon>
      </div>
      <div class="text-18 mt-32 text-center">
        {$t('All results recorded to date will be permanently deleted.')}
      </div>
      <div class="text-18 mt-24">
        {$t('Are you sure you want to continue?')}
      </div>
      <div class="flex gap-12 justify-between mt-24 w-full">
        <button class="border-blue-main border-2 text-blue-main rounded-md px-16 py-8 font-bold" on:click={() => showDeleteDataOverlay = false}>
          {$t('Cancel')}
        </button>
        <button class="border-blue-main border-2 text-blue-main rounded-md px-16 py-8 font-bold" on:click={() => { deleteData(deleteDataType); showDeleteDataOverlay = false; }}>
          {$t('reset')}
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showDeleteDashboardOverlay}
  <div class="fixed inset-0 flex items-start justify-center z-50 px-16 py-24 overflow-y-auto" style="background: rgba(0,0,0,0.5)">
    <div class="bg-white rounded-3xl relative p-24 flex flex-col w-full md:w-384 items-center">
      <div class="flex place-content-between mb-16">
        <div class="text-22 mt-12 font-bold">
          {$t('delete dashboard')}
        </div>
      </div>
      <div class="bg-false-light p8 rounded-3xl w-96 h-96 mt-24 flex items-center justify-center">
        <Icon name="delete" class="w-64 h-64 place-self-center text-white"></Icon>
      </div>
      <div class="text-18 mt-32 text-center">
        {$t('Access to your dashboard and all results recorded to date will be permanently deleted.')}
      </div>
      <div class="text-18 mt-24">
        {$t('Are you sure you want to continue?')}
      </div>
      <div class="flex gap-12 justify-between mt-24 w-full">
        <button class="border-blue-main border-2 text-blue-main rounded-md px-16 py-8 font-bold" on:click={() => showDeleteDashboardOverlay = false}>
          {$t('Cancel')}
        </button>
        <button class="border-blue-main border-2 text-blue-main rounded-md px-16 py-8 font-bold" on:click={() => { deleteDashboard(); showDeleteDashboardOverlay = false; }}>
          {$t('delete')}
        </button>
      </div>
    </div>
  </div>
{/if}

{#if openAfterResultsOverlay}
  <ResultsOverlay title="results after knowledge" results={aggAfter} ppl={afterPpl} onClose={() => openAfterResultsOverlay = false} />
{/if}

<div class="{dashboard.cycle == 'cycle1' ? 'bg-yellow-light' : 'bg-green-light'} w-full h-full fixed flex items-start flex-col overflow-y-auto">
    {#if showMode}
      <ChooseMode onClose="{closeMode}" {dashboard} {type}></ChooseMode>
    {/if}
    <div class="w-full max-w-1280 mt-24 md:mt-56 px-16 md:px-24 xl:px-32 mx-auto">
      <div class="flex justify-between w-full items-start gap-12">
        <div class="font-Caveat font-bold text-blue-main text-36 md:text-54">
          {$t('knowledge query')}
        </div>
        <Icon name="{sharedProps.currentLocale == 'de' ? 'logo-element-hero' : 'logo-element-hero-fr'}" class="w-96 md:w-128 xl:w-160 shrink-0 -mt-24 md:-mt-48"></Icon>
      </div>
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 xl:grid-rows-2 gap-16 md:gap-24 xl:min-h-[500px] mt-24 md:mt-32">
            <button class="bg-blue-main hover:bg-blue-hover rounded-3xl py-24 px-32 text-white font-bold text-22 flex flex-col text-left" on:click="{ () => openMode('prior')}">
                <div>
                    {$t('Test prior knowledge')}
                </div>
                <div class="mt-auto flex justify-end">
                    <div class="flex gap-12">{$t('show')}
                        <Icon name="arrow-circle-right" class="w-28 h-28 place-self-center"></Icon>
                    </div>
                </div>
            </button>
            <button class="bg-blue-main hover:bg-blue-hover rounded-3xl py-24 px-32 text-white font-bold text-22 flex flex-col text-left" on:click="{ () => openMode('after')}">
                <div>
                    {$t('Testing knowledge after learning')}
                </div>
                <div class="mt-auto flex justify-end">
                    <div class="flex gap-12">{$t('show')}
                        <Icon name="arrow-circle-right" class="w-28 h-28 place-self-center"></Icon>
                    </div>
                </div>
            </button>
            <div class="xl:row-span-2 md:col-span-2 xl:col-span-1 bg-white rounded-3xl py-24 px-24 flex h-fit xl:h-full flex-col">
              <div class="font-bold text-16 border-b border-line-subtle pb-8 mb-16">
                {dashboard.name}
              </div>
              {#if dashboard.cycle == 'cycle1'}
                <div class="text-18 text-yellow-accent">
                    {$t('cycle 1')}
                </div>
                <div class="text-18 font-bold text-yellow-accent">
                    {$t('Fire and fire prevention')}
                </div>
              {:else}
                <div class="text-18 text-green-main">
                    {$t('cycle 2')}
                </div>
                <div class="text-18 font-bold text-green-main">
                    {$t('Weather and natural hazards')}
                </div>
              {/if}
              <div class="mt-32 text-16 font-bold">
                {$t('topics:')}
              </div>
              <ul class="list-disc list-inside pl-0">
                {#each dashboard.categories as category}
                    <li>{category.name}</li>
                {/each}
              </ul>
              <div class="mt-48 xl:mt-auto">
                  <div class="space-y-12">
                      <button class="text-blue-main text-16 font-bold flex gap-12 text-left" on:click={() => openDeleteData("prior")}>{$t('Reset prior knowledge query')}
                        <Icon name="arrow-refresh-blue" class="w-28 h-28 shrink-0 place-self-center"></Icon>
                      </button>
                      <button class="text-blue-main text-16 font-bold flex gap-12 text-left" on:click={() => openDeleteData("after")}>{$t('Reset after knowledge query')}
                        <Icon name="arrow-refresh-blue" class="w-28 h-28 shrink-0 place-self-center"></Icon>
                      </button>
                  </div>
                  <div class="border-t border-line-subtle mt-24 pt-24">
                    <button class="text-blue-main text-16 font-bold flex gap-12 text-left" on:click={openDeleteDashboard}>{$t('Delete dashboard permanently')}
                      <Icon name="delete" class="w-28 h-28 shrink-0 place-self-center"></Icon>
                    </button>
                  </div>
              </div>
            </div>
            <div class="md:col-span-2 xl:col-span-2 bg-white rounded-3xl py-24 px-24 md:px-32 text-18 mb-24 xl:mb-0">
              <div class="font-bold text-22">{$t('test results')}</div>
              {#if aggPrior.length == 0 && aggAfter.length == 0}
              <div class="bg-line-subtle px-24 py-12 text-grey-1 rounded-md mt-16">
                <div class="">{$t('No results yet.')}</div>
                <div class="">{$t('Once the tests have been completed, a before and after comparison will be displayed here.')}</div>
              </div>
              <div class="flex gap-8 mt-10">
                <Icon name="info" class="w-28 h-28 place-self-center"></Icon>
                <div class="">{$t('You are free to perform only one of the tests.')}</div>
              </div>
              {:else}
                <div class="flex flex-col sm:flex-row flex-wrap gap-12 sm:gap-16">
                  <div>
                    <div class="text-16 mt-16">
                      {$t('prior knowledge')}
                    </div>
                    {#if aggPrior.length == 0}
                      <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-256 justify-center">
                        {$t('no results')}
                      </div>
                    {:else}
                      <div class="flex gap-1 mt-8">
                        <div class="flex flex-col bg-blue-light rounded-l-md py-12 px-16">
                          <div class="font-bold text-22">{priorPpl}</div>
                          <div class="text-18">SuS</div>
                        </div>
                        <div class="flex bg-blue-light rounded-r-md py-12 px-16 gap-32 items-center">
                          <div class="pl-8">
                            <div class="text-28 font-bold">{priorPercent}%</div>
                            <div class="text-18">{$t('correct')}</div>
                          </div>
                          <button class="bg-blue-main p-8 rounded-md h-fit" on:click={openPriorResults}>
                            <Icon name="arrow-up-right" class="w-28 h-28 place-self-center"></Icon>
                          </button>
                        </div>
                      </div>
                    {/if}
                  </div>
                  <div>
                    <div class="text-16 mt-16">
                      {$t('after knowledge')}
                    </div>
                    {#if aggAfter.length == 0}
                      <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-256 justify-center">
                        {$t('no results')}
                      </div>
                    {:else}
                      <div class="flex gap-1 mt-8">
                        <div class="flex flex-col bg-blue-light rounded-l-md py-12 px-16">
                          <div class="font-bold text-22">{afterPpl}</div>
                          <div class="text-18">SuS</div>
                        </div>
                        <div class="flex bg-blue-light rounded-r-md py-12 px-16 gap-32 items-center">
                          <div class="pl-8">
                            <div class="text-28 font-bold">{afterPercent}%</div>
                            <div class="text-18">{$t('correct')}</div>
                          </div>
                          <button class="bg-blue-main p-8 rounded-md h-fit" on:click={openAfterResults}>
                            <Icon name="arrow-up-right" class="w-28 h-28 place-self-center"></Icon>
                          </button>
                        </div>
                      </div>
                    {/if}
                  </div>
                  <div>
                    <div class="text-16 mt-16">
                      {$t('change')}
                    </div>
                    {#if aggPrior.length == 0}
                      <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-160 justify-center">
                        {$t('no prior results')}
                      </div>
                    {:else if aggAfter.length == 0}
                      <div class="bg-line-subtle text-grey-1 text-center items-center flex rounded-md h-84 mt-8 w-full sm:w-160 justify-center">
                        {$t('no after results')}
                      </div>
                    {:else if percentChange > 0}
                      <div class="flex gap-1 mt-8 bg-correct-light flex-col justify-center rounded-md py-12 px-16 w-full sm:w-180 items-center">
                        <div class="flex">
                          <Icon name="plus-simple" class="w-28 h-28 place-self-center"></Icon>
                          <div class="font-bold text-28">{percentChange}%</div>
                        </div>
                        <div class="text-18">{$t('improved')}</div>
                      </div>
                    {:else if percentChange < 0}
                      <div class="flex gap-1 mt-8 bg-false-light flex-col justify-center rounded-md py-12 px-16 w-full sm:w-180 items-center">
                        <div class="flex">
                          <Icon name="minus-simple" class="w-28 h-28 place-self-center"></Icon>
                          <div class="font-bold text-28">{Math.abs(percentChange)}%</div>
                        </div>
                        <div class="text-18">{$t('deteriorated')}</div>
                      </div>
                    {:else}
                      <div class="flex gap-1 mt-8 bg-line-subtle text-grey-1 flex-col justify-center rounded-md py-12 px-16 w-full sm:w-180 items-center">
                        <div class="font-bold text-28">0%</div>
                        <div class="text-18">{$t('no improvement')}</div>
                      </div>
                    {/if}
                  </div>
                </div>
              {/if}
            </div>
          </div>
    </div>
    <div class="mt-auto w-full"><Footer /></div>
</div>
